import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, NgZone, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs';
import { Tenant } from '../../models/tenant';
import { BlockAutofillDirective } from '../../directives/block-autofill.directive';
import { UserService } from '../../services/user.service';
import { ClickOrigin, HOVER_CAPABLE, clickOrigin } from '../../utils/device';
import { AvatarComponent } from '../avatar/avatar.component';
import {
  FAVORITES_ID,
  SwitcherEntry,
  SwitcherGroup,
  favoritesCount,
  flattenSections,
  formatCnpj,
  groupCompanies,
  groupLabel,
  hasFavorites,
  visibleGroups,
  visibleSections,
} from './company-switcher';

/**
 * Seletor de empresas em tela cheia de trabalho (modal): busca no topo (empresa, grupo ou CNPJ),
 * grupos à esquerda com contagem, empresas à direita com razão social e CNPJ.
 *
 * Tudo pelo teclado: o foco nasce na busca, ↑/↓ andam pelas empresas, Enter troca, Esc fecha.
 *
 * Escolher TROCA na hora. Já houve confirmação (popup SweetAlert, depois um 2º passo "de → para"
 * nesta janela) e a operação a tirou em 23/09/2026: quem abre o seletor e escolhe uma empresa já
 * decidiu — o passo extra era só mais um Enter em cada troca. A lista deixa claro onde se está
 * (selo "Atual") e o que se vai abrir; recarregar continua sendo do `app-company-select`.
 *
 * Favoritas: a estrela de cada linha grava no servidor (segue o usuário entre máquinas). Elas
 * aparecem numa seção no TOPO e continuam no grupo delas; a coluna de grupos ganha o recorte
 * "Favoritas" quando existe alguma.
 *
 * Foco: clique de mouse (estrela, grupo) devolve o foco pra busca, pro Enter seguinte continuar
 * trocando; no toque não (focar a busca abre o teclado virtual e ele come metade da lista).
 */
@Component({
  selector: 'app-company-switcher-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTooltip, AvatarComponent, BlockAutofillDirective],
  templateUrl: './company-switcher-modal.component.html',
  styleUrl: './company-switcher-modal.component.scss',
})
export class CompanySwitcherModalComponent implements AfterViewInit {
  @Input() set companies(value: Tenant[]) {
    this.groups = groupCompanies(value ?? []);
    this.total = value?.length ?? 0;
    this.refresh();
  }
  @Input() currentId?: number;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('list') list?: ElementRef<HTMLElement>;

  public readonly modal = inject(NgbActiveModal);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly userService = inject(UserService);
  public readonly formatCnpj = formatCnpj;
  public readonly groupLabel = groupLabel;
  public readonly FAVORITES_ID = FAVORITES_ID;
  // Toque não tem hover: tooltip em botão atrapalha o toque no iOS (ver utils/device.ts).
  public hoverCapable = HOVER_CAPABLE;

  public groups: SwitcherGroup[] = [];
  public total = 0;
  public term = '';
  /** Recorte: null = todos, FAVORITES_ID = só favoritas, ou o id de um grupo. */
  public groupId: number | null = null;
  /** Linha com o realce do teclado (índice em `flat`). */
  public activeIndex = 0;
  /** Seções na tela (recorte + busca). Recalculadas em refresh() a cada mudança de busca, recorte ou favorita. */
  public visible: SwitcherGroup[] = [];
  /** As mesmas linhas, planas e na ordem da tela: é o que as setas percorrem. */
  public flat: SwitcherEntry[] = [];
  public hasFavorites = false;
  /** Empresas com a gravação da estrela em voo: outro clique espera a resposta. */
  public readonly pending = new Set<number>();
  /** Troca pedida com estrela gravando: sai quando a gravação terminar (ver settle()). */
  private switchAfterSave?: Tenant;

  ngAfterViewInit(): void {
    this.playEnter();

    // Foco na busca: abrir o seletor é quase sempre pra digitar.
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      const start = this.initialIndex();
      this.activeIndex = start;
      if (start >= 0) this.scrollActiveIntoView();
    });
  }

  /** Contagem por grupo SÓ pela busca (ignora o recorte), pra coluna de grupos refletir o que achou. */
  public countFor(group: SwitcherGroup): number {
    return visibleGroups([group], null, this.term)[0]?.companies.length ?? 0;
  }

  public get favoritesTotal(): number {
    return favoritesCount(this.groups, this.term);
  }

  public get matchTotal(): number {
    return visibleGroups(this.groups, null, this.term).reduce((sum, g) => sum + g.companies.length, 0);
  }

  /** Posição da linha na lista plana: a favorita tem duas linhas, então conta a seção. */
  public indexOf(sectionId: number, company: Tenant): number {
    return this.flat.findIndex((entry) => entry.sectionId === sectionId && entry.company.id === company.id);
  }

  public onTermChange(): void {
    this.refresh();
    this.activeIndex = 0;
    // Busca que tira o grupo selecionado do resultado volta pra "todos" — senão a lista ficaria
    // vazia com o resultado escondido no grupo ao lado.
    if (this.groupId !== null && !this.visible.length && this.matchTotal > 0) {
      this.groupId = null;
      this.refresh();
    }
  }

  public selectGroup(groupId: number | null, event?: MouseEvent): void {
    this.groupId = groupId;
    this.activeIndex = 0;
    this.refresh();
    // Mouse e teclado voltam pra busca (setas + Enter seguem trocando); toque não.
    if (!event || clickOrigin(event, this.hoverCapable) !== 'touch') this.searchInput?.nativeElement.focus();
  }

  public clearSearch(): void {
    this.term = '';
    this.onTermChange();
    this.searchInput?.nativeElement.focus();
  }

  public onKeydown(event: KeyboardEvent): void {
    // Enter num botão (estrela, grupo, Limpar, ×, a própria linha) é do botão: o clique nativo
    // resolve. Sem isso o Enter na estrela trocava de empresa em vez de favoritar.
    if (event.key === 'Enter' && (event.target as HTMLElement | null)?.closest?.('button')) return;

    const last = this.flat.length - 1;
    if (last < 0) return;

    if (event.key === 'ArrowDown') this.activeIndex = Math.min(last, this.activeIndex + 1);
    else if (event.key === 'ArrowUp') this.activeIndex = Math.max(0, this.activeIndex - 1);
    else if (event.key === 'Home') this.activeIndex = 0;
    else if (event.key === 'End') this.activeIndex = last;
    else if (event.key === 'Enter') {
      event.preventDefault();
      this.choose(this.flat[this.activeIndex]?.company);
      return;
    } else return;

    event.preventDefault();
    this.scrollActiveIntoView();
  }

  /** Escolher É trocar: o modal fecha com a empresa e o gatilho recarrega nela. */
  public choose(company: Tenant | undefined): void {
    if (!company) return;
    // Escolher a empresa em que já se está só fecha: não há troca.
    if (company.id === this.currentId) {
      this.modal.dismiss();
      return;
    }

    // Estrela ainda gravando: a troca recarrega a página e abortaria a gravação — a estrela acesa
    // voltaria apagada depois do reload, sem aviso. Fecha quando a resposta chegar.
    if (this.pending.size) {
      this.switchAfterSave = company;
      return;
    }

    this.modal.close(company);
  }

  /**
   * Estrela: troca na hora e grava em seguida; se a gravação falhar, volta (o toast de erro é do
   * BaseService). Uma gravação por empresa de cada vez — com duas réplicas da API, cliques rápidos
   * (liga, desliga) podiam chegar fora de ordem e deixar o servidor diferente da tela.
   */
  public toggleFavorite(company: Tenant, event?: MouseEvent): void {
    const origin = event ? clickOrigin(event, this.hoverCapable) : 'keyboard';
    // Clique de MOUSE devolve o foco pra busca: o Enter seguinte continua trocando pra empresa
    // realçada, em vez de cair na estrela e desfazer a favorita. Teclado: o foco fica na estrela.
    if (origin === 'mouse') this.searchInput?.nativeElement.focus();
    // 2º clique de um clique duplo: na seção Favoritas a linha de baixo já subiu pra debaixo do
    // cursor, e ele desfavoritaria OUTRA empresa.
    if (event && event.detail > 1) return;
    if (this.pending.has(company.id)) return;

    const next = !company.isFavorite;
    this.applyFavorite(company, next, origin);
    this.pending.add(company.id);

    this.userService
      .setCompanyFavorite(company.id, next)
      .pipe(finalize(() => this.settle(company.id)))
      .subscribe({
        error: () => {
          // Falhou: a troca pedida no meio da gravação não sai — a pessoa vê a estrela voltar e o toast.
          this.switchAfterSave = undefined;
          this.applyFavorite(company, !next);
        },
      });
  }

  /**
   * Linha de baixo do nome: a razão social quando difere do apelido. Na seção Favoritas as
   * empresas vêm de grupos diferentes, então o grupo vai na frente.
   */
  public subtitle(sectionId: number, company: Tenant): string {
    const legal = company.fantasyName && company.name && company.fantasyName !== company.name ? company.name : '';
    if (sectionId !== FAVORITES_ID) return legal;
    return [groupLabel(company), legal].filter(Boolean).join(' · ');
  }

  /**
   * Rótulo FIXO de botão de alternância: o estado vai no aria-pressed. Rótulo que trocava junto
   * ("Remover das favoritas") fazia o leitor de tela dizer o estado duas vezes, e contraditório.
   */
  public favoriteLabel(company: Tenant): string {
    return `Favorita: ${company.fantasyName || company.name}`;
  }

  private settle(companyId: number): void {
    this.pending.delete(companyId);
    if (!this.pending.size && this.switchAfterSave) this.modal.close(this.switchAfterSave);
  }

  // O Tenant é o mesmo objeto da lista do app-company-select: a próxima abertura já vem certa,
  // sem buscar a lista de novo. `origin` vazio = desfazer por erro de gravação (sem clique).
  private applyFavorite(company: Tenant, favorite: boolean, origin?: ClickOrigin): void {
    const active = this.flat[this.activeIndex];
    company.isFavorite = favorite;
    this.refresh();

    // Recorte "Favoritas" que ficou vazio volta pra todos (não fica uma lista em branco).
    if (this.groupId === FAVORITES_ID && !this.visible.length) {
      this.groupId = null;
      this.refresh();
    }

    this.keepFocus(origin ? origin !== 'touch' : this.hoverCapable);

    // O realce fica na MESMA linha: a seção de favoritas cresce ou encolhe acima dela.
    const kept = active ? this.indexOf(active.sectionId, active.company) : -1;
    this.activeIndex = kept >= 0 ? kept : Math.min(this.activeIndex, Math.max(0, this.flat.length - 1));
  }

  /**
   * Foco numa linha que saiu da lista (desfavoritar pelo teclado na seção Favoritas ou no recorte):
   * o navegador jogaria o foco no body, fora do seletor, e setas, Enter e Esc parariam de responder.
   * Passa pra estrela vizinha da mesma seção — já está na tela e continua lá — ou, sem vizinha, pra
   * busca (menos no toque: abriria o teclado virtual).
   * Roda depois do refresh() e antes de a tela redesenhar: a linha que vai sair ainda está no DOM.
   */
  private keepFocus(canFocusSearch: boolean): void {
    const list = this.list?.nativeElement;
    const row = document.activeElement?.closest<HTMLElement>('.switcher-row');
    if (!list || !row || !list.contains(row)) return;

    const stays = (r: HTMLElement): boolean =>
      this.flat.some((e) => e.sectionId === Number(r.dataset['section']) && e.company.id === Number(r.dataset['company']));
    if (stays(row)) return;

    const section = Array.from(list.querySelectorAll<HTMLElement>(`.switcher-row[data-section="${row.dataset['section']}"]`));
    const at = section.indexOf(row);
    const neighbor = section.slice(at + 1).find(stays) ?? section.slice(0, at).reverse().find(stays);
    const target = neighbor?.querySelector<HTMLElement>('.switcher-fav') ?? (canFocusSearch ? this.searchInput?.nativeElement : undefined);
    target?.focus();
  }

  // Realce na empresa atual: Enter logo ao abrir só fecha, como antes das favoritas (a troca
  // recarrega a página — Enter por reflexo não pode trocar de empresa). A seção Favoritas vem antes
  // dos grupos, então a atual favorita é achada ali, no topo, com as outras favoritas à vista.
  //
  // Com favoritas e a atual FORA delas, abre sem realce (-1) e no topo: rolar até a atual escondia o
  // acesso rápido justamente de quem montou um. Sem realce, Enter não faz nada (a troca por reflexo
  // continua impossível) e a 1ª seta cai na primeira favorita.
  private initialIndex(): number {
    const current = this.flat.findIndex((e) => e.company.id === this.currentId);
    if (this.hasFavorites && this.groupId === null && this.flat[current]?.sectionId !== FAVORITES_ID) return -1;
    return current;
  }

  private refresh(): void {
    this.hasFavorites = hasFavorites(this.groups);
    this.visible = visibleSections(this.groups, this.groupId, this.term);
    this.flat = flattenSections(this.visible);
  }

  /**
   * Entrada do seletor (CSS em styles.scss, .company-switcher-dialog). Começa DOIS quadros depois de
   * montar: o primeiro quadro (lista, avatares, estrelas) é o caro e acontece com a janela ainda
   * invisível; a animação, só opacity e transform, roda inteira depois dele. Começar junto era o que
   * fazia a abertura parecer travada no Firefox.
   */
  private playEnter(): void {
    const dialog = this.host.nativeElement.closest('.modal-dialog');
    const backdrop = document.querySelector('ngb-modal-backdrop.company-switcher-backdrop');
    this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          dialog?.classList.add('is-open');
          backdrop?.classList.add('is-open');
        }),
      ),
    );
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      this.list?.nativeElement
        .querySelector<HTMLElement>('.switcher-company.is-active')
        ?.scrollIntoView({ block: 'nearest' });
    });
  }
}
